'use client'

import React, { useTransition } from 'react'
import { useQuery } from '@tanstack/react-query'
import { signOut } from 'next-auth/react'
import { useFormStatus } from 'react-dom'
import { toast } from 'sonner'
import {
  closeRegister,
  getCloseRegisterHtml,
  getRegisterSummary,
  setCookieCartId,
} from '@/lib/actions'
import {
  useApp,
  useCloseRegisterPrinter,
  useModals,
  useUser,
} from '@/lib/hooks'
import { usePersistor } from '@/components/providers/store-provider'
import { cn, formatPrice } from '@/lib/utils'

import {
  Badge,
  Button,
  InputForm,
  LoadingDots,
  ScrollArea,
} from '@/components/ui'

interface CloseRegisterProps extends React.ComponentPropsWithoutRef<'div'> {
  open?: boolean
}

export const CloseRegister = ({ className, open }: CloseRegisterProps) => {
  if (!open) return null

  const { requestPrinter, printer, printReceipt } = useCloseRegisterPrinter()
  const { closeAll } = useModals()
  const [isHtmlPrinting, startHtmlPrinting] = useTransition()
  const { setUserPin, setUserToken } = useUser()
  const { setIsLoading } = useApp()
  const persistor = usePersistor()

  const getCookieValue = (name: string): string | null => {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [key, value] = cookie.trim().split('=')
      if (key === name) {
        return decodeURIComponent(value)
      }
    }
    return null
  }

  const printerType = getCookieValue('printerType') || 'thermal'

  // Fetch register summary using React Query
  const { data: summaryData, isLoading: isPendingSummary } = useQuery({
    queryKey: ['register-summary'],
    queryFn: async () => {
      const result = await getRegisterSummary()
      return result
    },
    staleTime: 0,
    gcTime: 0, // Disable caching completely
    enabled: !!open,
    refetchOnMount: 'always',
  })

  const summary = summaryData?.summary ?? []
  const totalTransactions = summaryData?.total_transactions_amount?.[0]

  const handleRegister = async (closingBalance: any) => {
    try {
      await setCookieCartId('')
      await closeRegister(closingBalance)

      try {
        // Clear Redux persisted cache before signing out
        await persistor.purge()

        closeAll(() => {
          setUserPin([])
          setUserToken('')
          setIsLoading(false)
          signOut()
        })
      } catch (error) {
        toast.error(`Couldn't print receipt. Please check printer connection.`)
        return
      }
    } catch (error) {
      toast.error(`Couldn't close register.`)
    }
  }

  const handleSignOut = async (data: FormData) => {
    const closingBalance = data.get('closing-balance')
    await handleRegister(closingBalance)
  }

  const printNow = async () => {
    startHtmlPrinting(async () => {
      try {
        const html = await getCloseRegisterHtml()

        if (html?.url) {
          window.open(html?.url, '_blank')
        } else {
          toast.error('Failed to generate printable pdf')
        }
      } catch (error) {
        toast.error('Failed to generate printable pdf')
      }
    })
  }

  return (
    <form
      action={handleSignOut}
      className={cn('flex w-full flex-col gap-5', className)}
    >
      <div className="flex flex-col gap-2">
        <InputForm
          autoCorrect="off"
          autoComplete="off"
          name="closing-balance"
          placeholder="10,000.00"
          className="lg:h-[44px]"
          icon={<span className="font-mono font-bold">Rs.</span>}
          type="number"
        />
      </div>

      {isPendingSummary && (
        <div className="flex h-[300px] w-full items-center justify-center">
          <LoadingDots />
        </div>
      )}

      {!isPendingSummary && summary?.length === 0 && (
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg border border-gray-100 text-sm text-gray-700">
          No transactions found.
        </div>
      )}

      {summary && summary?.length !== 0 && (
        <ScrollArea className="h-[300px]">
          <div className="flex w-full flex-col gap-1 pr-4 lg:pr-[unset]">
            {summary?.map(
              (
                {
                  payment_method,
                  currency,
                  transaction_total,
                  transaction_count,
                }: {
                  payment_method: string
                  currency: string
                  transaction_total: number
                  transaction_count: number
                },
                index: number,
              ) => {
                return (
                  <div
                    key={index}
                    className={cn(
                      'group flex w-full select-none items-center justify-between gap-4 rounded-md border border-gray-100 border-gray-100/80 bg-white px-3 py-2 shadow-card transition-colors dark:border-gray-700 hover:dark:border-gray-700',
                      className,
                    )}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {payment_method}
                        </span>
                        <Badge>{currency}</Badge>
                      </div>
                      <span className="text-xs text-gray-700">
                        {transaction_count} Transaction
                        {transaction_count !== 1 && 's'}
                      </span>
                    </div>

                    <div className="flex items-center gap-0.5">
                      <span className="font-mono">
                        {formatPrice(transaction_total, 2, false)}
                      </span>
                      <span className="font-mono">{currency}</span>
                    </div>
                  </div>
                )
              },
            )}

            <div
              className={cn(
                'group flex w-full select-none items-center justify-between gap-4 rounded-md border border-gray-100 border-gray-100/80 bg-white px-3 py-2 shadow-card transition-colors dark:border-gray-700 hover:dark:border-gray-700',
                className,
              )}
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    Total Transactions
                  </span>
                  <Badge>{totalTransactions?.currency}</Badge>
                </div>
                <span className="text-xs text-gray-700">
                  {totalTransactions?.transaction_count} Transaction
                  {totalTransactions?.transaction_count !== 1 && 's'}
                </span>
              </div>

              <div className="flex items-center gap-0.5">
                <span className="font-mono">
                  {formatPrice(totalTransactions?.transaction_total, 2, false)}
                </span>
                <span className="font-mono">{totalTransactions?.currency}</span>
              </div>
            </div>
          </div>
        </ScrollArea>
      )}

      <Actions
        requestPrinter={requestPrinter}
        printer={printer}
        printReceipt={printReceipt}
        printNow={printNow}
        printerType={printerType}
        isHtmlPrinting={isHtmlPrinting}
        isDataLoading={isPendingSummary}
      />
    </form>
  )
}

const Actions = ({ printNow, isHtmlPrinting, isDataLoading }: any) => {
  const { pending } = useFormStatus()
  const isDisabled = pending || isDataLoading

  return (
    <div className="flex w-full items-center gap-2">
      <Button
        variant="outline"
        className="w-full"
        size="lg"
        disabled={isDisabled || isHtmlPrinting}
        onClick={printNow}
      >
        {isHtmlPrinting ? 'Generating PDF...' : 'Print'}
      </Button>

      <Button type="submit" className="w-full" size="lg" disabled={isDisabled}>
        {pending ? 'Closing' : isDataLoading ? 'Loading...' : 'Close Register'}
      </Button>
    </div>
  )
}
